import React from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Calendar, Zap, Timer, AlertTriangle, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { STATUS_META, PRIORITY_META, CONCEPT, TIMELINE_STATUS_META } from "@/lib/constants";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function Card({ className, children, ...rest }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-white/10 bg-[#0F172A] shadow-md shadow-black/30 transition-colors duration-150 hover:border-[#64748B]/40",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Button({ variant = "primary", size = "md", className, children, ...rest }) {
  const variants = {
    primary: "bg-[#F59E0B] text-[#0F172A] hover:bg-[#FBBF24] font-bold shadow-sm shadow-[#F59E0B]/25 active:scale-[0.98]",
    secondary: "border border-[#64748B]/40 bg-[#64748B]/10 text-[#E2E8F0] hover:bg-[#64748B]/20 active:scale-[0.98]",
    ghost: "text-slate-300 hover:bg-white/10 hover:text-white",
    danger: "bg-[#DC2626] text-white hover:bg-[#EF4444] active:scale-[0.98]",
    success: "bg-[#0F172A] border border-[#64748B]/50 text-[#E2E8F0] hover:bg-[#1E293B] active:scale-[0.98]",
  };
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-base" };
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md font-semibold uppercase tracking-wide transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed",
        variants[variant],
        sizes[size],
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Input({ className, ...rest }) {
  return (
    <input
      className={cn(
        "w-full rounded-md border border-white/10 bg-[#020617] px-3.5 py-2 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-[#64748B] focus:ring-1 focus:ring-[#64748B]/40",
        className
      )}
      {...rest}
    />
  );
}

export function Textarea({ className, ...rest }) {
  return (
    <textarea
      className={cn(
        "w-full rounded-md border border-white/10 bg-[#020617] px-3.5 py-2 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-[#64748B] focus:ring-1 focus:ring-[#64748B]/40",
        className
      )}
      {...rest}
    />
  );
}

// Internal: renders a dropdown panel via portal so it escapes overflow/scroll
// containers (e.g. inside Modal). Fixed-positioned relative to the anchor.
function DropdownPortal({ anchorRef, panelRef, open, children, className }) {
  const [pos, setPos] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const spaceBelow = window.innerHeight - r.bottom;
      const flipUp = spaceBelow < 260 && r.top > spaceBelow;
      setPos({
        left: r.left,
        width: r.width,
        top: flipUp ? undefined : r.bottom + 4,
        bottom: flipUp ? window.innerHeight - r.top + 4 : undefined,
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={panelRef}
      className={cn("max-h-64 overflow-y-auto rounded-md border border-white/10 bg-[#0F172A] shadow-xl shadow-black/50", className)}
      style={{ position: "fixed", zIndex: 70, left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
    >
      {children}
    </div>,
    document.body
  );
}

// Custom dropdown styled like UserSelect. Accepts <option> children and calls
// onChange with { target: { value } } so existing call sites keep working.
export function Select({ className, children, value, onChange, placeholder, disabled, ...rest }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const panelRef = React.useRef(null);

  const options = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === "option") {
      options.push({ value: child.props.value ?? child.props.children, label: child.props.children, disabled: child.props.disabled });
    } else {
      // e.g. <optgroup> or fragments — flatten nested children
      React.Children.forEach(child.props?.children, (c) => {
        if (React.isValidElement(c) && c.type === "option") {
          options.push({ value: c.props.value ?? c.props.children, label: c.props.children, disabled: c.props.disabled });
        }
      });
    }
  });

  const selected = options.find((o) => String(o.value) === String(value ?? ""));

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const pick = (v) => {
    // Mimic native <select> behavior: values are always strings.
    onChange?.({ target: { value: v == null ? "" : String(v) } });
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={cn("relative w-full", className)} {...rest}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-md border border-white/10 bg-[#020617] px-3 py-2 text-left text-sm text-slate-100 outline-none transition-colors focus:border-[#64748B] focus:ring-1 focus:ring-[#64748B]/40 disabled:opacity-50"
      >
        <span className={cn("truncate", !selected && "text-slate-500")}>
          {selected ? selected.label : placeholder ?? options[0]?.label ?? "—"}
        </span>
        <ChevronDown size={14} className={cn("ml-auto text-slate-500 transition-transform", open && "rotate-180")} />
      </button>
      <DropdownPortal anchorRef={rootRef} panelRef={panelRef} open={open}>
        {options.map((o, i) => (
          <button
            key={i}
            type="button"
            disabled={o.disabled}
            onClick={() => pick(o.value ?? "")}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/5 disabled:opacity-50",
              String(o.value ?? "") === String(value ?? "") ? "bg-[#64748B]/15 text-[#E2E8F0]" : "text-slate-200"
            )}
          >
            <span className="truncate">{o.label}</span>
          </button>
        ))}
      </DropdownPortal>
    </div>
  );
}

// Custom dropdown that shows each user's avatar + name (native <select> can't render images)
export function UserSelect({ users = [], value, onChange, placeholder = "— Unassigned —", className, ...rest }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const selected = users.find((u) => String(u.id) === String(value));

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const pick = (id) => {
    onChange?.(id);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={cn("relative", className)} {...rest}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-md border border-white/10 bg-[#020617] px-3 py-2 text-left text-sm text-slate-100 outline-none transition-colors focus:border-[#64748B] focus:ring-1 focus:ring-[#64748B]/40"
      >
        {selected ? (
          <>
            <Avatar src={selected.avatar_url} name={selected.name} size={22} />
            <span className="truncate">{selected.name}</span>
          </>
        ) : (
          <span className="truncate text-slate-500">{placeholder}</span>
        )}
        <ChevronDown size={14} className={cn("ml-auto text-slate-500 transition-transform", open && "rotate-180")} />
      </button>
      <DropdownPortal anchorRef={rootRef} panelRef={panelRef} open={open}>
        <button
          type="button"
          onClick={() => pick("")}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-400 hover:bg-white/5"
        >
          {placeholder}
        </button>
        {users.map((u) => (
          <button
            key={u.id}
            type="button"
            onClick={() => pick(String(u.id))}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/5",
              String(u.id) === String(value) ? "bg-[#64748B]/15 text-[#E2E8F0]" : "text-slate-200"
            )}
          >
            <Avatar src={u.avatar_url} name={u.name} size={22} />
            <div className="min-w-0">
              <p className="truncate">{u.name}</p>
              {u.job_title && <p className="truncate text-[10px] text-slate-500">{u.job_title}</p>}
            </div>
          </button>
        ))}
      </DropdownPortal>
    </div>
  );
}

export function Label({ children, className }) {
  return (
    <label className={cn("mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#94A3B8]", className)}>
      {children}
    </label>
  );
}

export function Tooltip({ label, children, className }) {
  return (
    <div className={cn("group relative inline-flex", className)}>
      {children}
      <span className="pointer-events-none absolute -top-1.5 left-1/2 z-20 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-slate-800 px-2.5 py-1 text-[11px] font-medium text-slate-200 opacity-0 shadow-lg ring-1 ring-slate-700 transition-opacity duration-150 group-hover:opacity-100">
        {label}
        <span className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-4 border-t-4 border-x-transparent border-t-slate-800" />
      </span>
    </div>
  );
}

export function Avatar({ src, name, size = 32, className }) {
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return src ? (
    <img
      src={src}
      alt={name}
      style={{ width: size, height: size }}
      className={cn("rounded-full object-cover ring-2 ring-slate-800", className)}
    />
  ) : (
    <div
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      className={cn("flex items-center justify-center rounded-full bg-gradient-to-br from-[#1E293B] to-[#64748B] font-semibold text-white ring-2 ring-[#64748B]/25", className)}
    >
      {initials}
    </div>
  );
}

export function StatusBadge({ status, className, ...rest }) {
  const m = STATUS_META[status] || STATUS_META.backlog;
  return (
    <span
      style={{ color: m.color, backgroundColor: m.bg, borderColor: m.color + "55" }}
      className={cn("inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide", className)}
      {...rest}
    >
      {m.label}
    </span>
  );
}

// Timeline status badge — dimensi terpisah dari workflow StatusBadge
export function TimelineStatusBadge({ timelineStatus, className, ...rest }) {
  const m = TIMELINE_STATUS_META[timelineStatus];
  if (!m || timelineStatus === "no_deadline") return null;
  return (
    <span
      style={{ color: m.color, backgroundColor: m.bg, borderColor: m.color + "55" }}
      className={cn("inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide", className)}
      {...rest}
    >
      {m.label}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span
      style={{ color: m.color, borderColor: m.color + "55" }}
      className="inline-flex items-center gap-1 rounded-sm border bg-[#020617] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
    >
      {priority === "urgent" && <AlertTriangle size={10} />}
      {m.label}
    </span>
  );
}

// Effort Weight (Indigo) — dominant operational metric, drives workload & scoring
export function WeightBadge({ weight, className }) {
  return (
    <span
      style={{ color: CONCEPT.weight.color, backgroundColor: CONCEPT.weight.bg, borderColor: CONCEPT.weight.border }}
      className={cn("inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[11px] font-semibold tabular-nums", className)}
      title="Bobot pekerjaan (dasar workload & scoring)"
    >
      <Zap size={11} />
      {weight != null ? `${weight} pts` : "— pts"}
    </span>
  );
}

// Timeline (Sky) — deadline planning
export function TimelineBadge({ date, className }) {
  if (!date) return null;
  const d = new Date(date);
  const str = d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
  return (
    <span
      style={{ color: CONCEPT.timeline.color, backgroundColor: CONCEPT.timeline.bg, borderColor: CONCEPT.timeline.border }}
      className={cn("inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium", className)}
      title="Deadline / timeline"
    >
      <Calendar size={11} />
      {str}
    </span>
  );
}

// Actual Time (Muted Slate) — subdued, analytics only, never competes with Weight
export function ActualTimeBadge({ hours, className }) {
  return (
    <span
      style={{ color: CONCEPT.actual.color, backgroundColor: CONCEPT.actual.bg, borderColor: CONCEPT.actual.border }}
      className={cn("inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[11px] tabular-nums", className)}
      title="Actual time (hanya untuk analitik)"
    >
      <Timer size={11} />
      {hours != null ? `${hours}h` : "—"}
    </span>
  );
}

export function ProgressBar({ value, max = 100, color = "#E2E8F0", className, height = 8 }) {
  const pct = Math.min(100, max > 0 ? (value / max) * 100 : 0);
  return (
    <div className={cn("w-full overflow-hidden rounded-sm bg-[#020617]", className)} style={{ height }}>
      <div
        className="h-full rounded-sm transition-all duration-500 ease-out"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

export function Spinner({ className }) {
  return (
    <div className={cn("flex items-center justify-center py-16", className)}>
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#F59E0B]/20 border-t-[#F59E0B]" />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
      {Icon && <Icon className="text-slate-600" size={36} />}
      <p className="font-heading text-lg text-slate-300">{title}</p>
      {subtitle && <p className="max-w-sm text-sm text-slate-500">{subtitle}</p>}
    </div>
  );
}

export function usePagination(items, perPage = 15) {
  const [page, setPage] = React.useState(1);
  const total = items?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(page, totalPages);
  const paged = React.useMemo(
    () => (items || []).slice((safePage - 1) * perPage, safePage * perPage),
    [items, safePage, perPage]
  );
  return { page: safePage, setPage, totalPages, paged, total, perPage };
}

export function Pagination({ page, totalPages, total, perPage, onChange }) {
  if (!totalPages || totalPages <= 1) return null;
  const start = (page - 1) * perPage + 1;
  const end = Math.min(total, page * perPage);

  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= 1) pages.push(p);
    else if (pages[pages.length - 1] !== "…") pages.push("…");
  }

  const btn = "flex h-7 min-w-[28px] items-center justify-center rounded-md px-2 text-xs font-medium transition-colors disabled:opacity-40";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-4 py-3 text-xs text-slate-400" data-testid="pagination">
      <span>Menampilkan {start}–{end} dari {total}</span>
      <div className="flex items-center gap-1">
        <button type="button" className={cn(btn, "text-slate-300 hover:bg-white/5")} disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label="Halaman sebelumnya">
          <ChevronLeft size={14} />
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className="px-1.5 text-slate-600">…</span>
          ) : (
            <button key={p} type="button" onClick={() => onChange(p)} data-testid={`pagination-page-${p}`}
              className={cn(btn, p === page ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5")}>
              {p}
            </button>
          )
        )}
        <button type="button" className={cn(btn, "text-slate-300 hover:bg-white/5")} disabled={page >= totalPages} onClick={() => onChange(page + 1)} aria-label="Halaman berikutnya">
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, size = "md", footer }) {
  if (!open) return null;
  const sizes = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl" };
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:items-center"
      data-testid="modal-overlay"
    >
      <div
        data-testid="modal-content"
        className={cn("relative w-full animate-fade-up rounded-xl border border-white/10 bg-[#0F172A] shadow-2xl shadow-black/60", sizes[size])}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 bg-[#020617] px-5 py-4">
          <h3 className="font-heading text-lg font-bold text-slate-100">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="modal-close-button">
            ✕
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-white/10 bg-[#020617] px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}
